import {
  BottomSheet,
  Illustration,
  Text,
  Title,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { openInBrowser } from "@src/appV2/lib/adapters/capacitorBrowser.adapter";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { CommonDialog } from "@src/appV2/lib/Dialogs/Common/Dialog";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format } from "date-fns";
import { useState } from "react";

import { type ActiveBookingReorderingCohort } from "../../PlacementCandidate/types";
import { useNotifyRecruiterForScreening } from "../hooks/useNotifyRecruiterForScreening";
import { usePlacementRecruiterScreeningConfig } from "../hooks/usePlacementRecruiterScreeningConfig";

interface BookFirstInterviewConfirmationDialogProps {
  modalState: UseModalState;
  interviewDate?: Date;
  placementId?: string;
  source?: "banner" | "detail-cta";
  bookingReorderingCohort?: ActiveBookingReorderingCohort;
}

function getFormattedDayBeforeInterview(interviewDate?: Date): string {
  if (!interviewDate) {
    return "the day before your interview";
  }

  const dayBefore = new Date(interviewDate);
  dayBefore.setDate(dayBefore.getDate() - 1);

  return format(dayBefore, "EEEE, MMMM d");
}

export function BookFirstInterviewConfirmationDialog(
  props: BookFirstInterviewConfirmationDialogProps
) {
  const {
    modalState,
    interviewDate,
    placementId,
    source = "detail-cta",
    bookingReorderingCohort,
  } = props;
  const { screeningCalendlyLink } = usePlacementRecruiterScreeningConfig();
  const exitConfirmationModalState = useModalState();
  const worker = useDefinedWorker();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const dayBeforeInterview = getFormattedDayBeforeInterview(interviewDate);

  const { mutateAsync: notifyRecruiterForScreening, isLoading: isNotifyingRecruiter } =
    useNotifyRecruiterForScreening({
      onSuccess: () => {
        setShowConfirmation(true);
      },
    });

  const handleClose = () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_BOOK_FIRST_EXIT_CLICKED, {
      source,
      ...(isDefined(placementId) && { placementId }),
      ...(isDefined(bookingReorderingCohort) && { bookingReorderingCohort }),
    });
    exitConfirmationModalState.openModal();
  };

  const handleCallMeNow = async () => {
    await notifyRecruiterForScreening({
      source,
      contactPhone: worker.phone ?? "",
      ...(isDefined(placementId) && { placementId }),
    });
  };

  const handleScheduleLater = async () => {
    if (screeningCalendlyLink) {
      logEvent(APP_V2_APP_EVENTS.PLACEMENT_BOOK_FIRST_SCHEDULE_LATER_CLICKED, {
        source,
        ...(isDefined(placementId) && { placementId }),
        ...(isDefined(bookingReorderingCohort) && { bookingReorderingCohort }),
      });

      await openInBrowser(screeningCalendlyLink);
    }

    modalState.closeModal();
  };

  return (
    <>
      <BottomSheet
        modalState={modalState}
        header={<BottomSheetHeader onClose={handleClose} />}
        onClose={handleClose}
      >
        <Box sx={{ px: 5, py: 6 }}>
          {showConfirmation ? (
            <Box sx={{ px: 6, py: 8 }}>
              <Stack spacing={8} alignItems="center">
                <Illustration type="phone" />
                <Text variant="h2" align="center">
                  Speak to you soon!
                </Text>
                <Stack alignItems="center" spacing={2}>
                  <Text variant="body1">We&apos;ll call you in:</Text>
                  <Text bold variant="body1">
                    15 minutes or less
                  </Text>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <Stack spacing={10} alignItems="center" justifyContent="center">
              <Stack spacing={6} alignItems="center" justifyContent="center">
                <Illustration type="phone" />

                <Text variant="h2" textAlign="center">
                  Interview booked - one step left!
                </Text>
              </Stack>
              <Stack spacing={4} sx={{ paddingX: 4 }}>
                <Text variant="body1" textAlign="center" sx={{ fontSize: 18 }}>
                  You haven&apos;t done your one-time, 15-minute screening call with a Clipboard
                  recruiter yet.
                </Text>
                <Text variant="body1" textAlign="center" sx={{ fontSize: 18 }}>
                  Complete it now to keep your in-person interview and{" "}
                  <strong>unlock instant booking for hundreds of jobs</strong> - no extra steps next
                  time.
                </Text>
              </Stack>
              <Stack spacing={4} sx={{ width: "100%" }}>
                <LoadingButton
                  fullWidth
                  variant="contained"
                  size="large"
                  isLoading={isNotifyingRecruiter}
                  onClick={handleCallMeNow}
                >
                  Complete my call now
                </LoadingButton>

                <Button fullWidth variant="outlined" size="large" onClick={handleScheduleLater}>
                  Schedule the call for later
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </BottomSheet>

      <CommonDialog
        modalState={exitConfirmationModalState}
        title={
          <Title bold component="h3">
            Are you sure you want to exit?
          </Title>
        }
        actions={
          <Stack width="100%" spacing={1}>
            <Button
              variant="contained"
              onClick={() => {
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_BOOK_FIRST_EXIT_DIALOG_CONFIRMED, {
                  source,
                  ...(isDefined(placementId) && { placementId }),
                  ...(isDefined(bookingReorderingCohort) && { bookingReorderingCohort }),
                });
                exitConfirmationModalState.closeModal();
                modalState.closeModal();
              }}
            >
              Yes, exit
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_BOOK_FIRST_EXIT_DIALOG_CANCELLED, {
                  source,
                  ...(isDefined(placementId) && { placementId }),
                  ...(isDefined(bookingReorderingCohort) && { bookingReorderingCohort }),
                });
                exitConfirmationModalState.closeModal();
              }}
            >
              Cancel
            </Button>
          </Stack>
        }
      >
        <Text sx={{ lineHeight: "24px" }} textAlign="center">
          If you don&apos;t complete a screening call by 2:00 pm on {dayBeforeInterview}, we&apos;ll
          have to cancel your interview.
        </Text>
      </CommonDialog>
    </>
  );
}
